/**
 * Ключ за идемпотентност на действие с НЕИЗВЕСТЕН изход (одит 01.10.2026, N-01).
 *
 * Сервитьорът натиска „Поръчай", отговорът се губи (таймаут, 5xx, връзката пада
 * точно при прехода облак → каса) и той натиска пак. Без ключ второто натискане
 * беше ВТОРА поръчка: втори кухненски билет и двойна сума — онлайн, на касата и
 * когато първото е минало в облака, а повторението — на касата офлайн.
 *
 * Правилото: същото тяло, повторено СЛЕД провал с неизвестен изход, носи
 * същия ключ; успех или категоричен отказ (4xx) го забравят — следващата
 * еднаква поръчка е нова и законна. Ключът е в ТЯЛОТО (`idempotencyKey`), не в
 * хедър: облакът в production пуска през CORS само `Authorization` и
 * `Content-Type`, а непознато поле в тялото всяка версия на облака и на касата
 * пренебрегва. Касата го пази в дневника и той стига до облака с replay-а.
 *
 * Автоматично повторение на мутация НЯМА и сега (`main.tsx`: `retry: 0`,
 * `client.ts` §Повторение след смяна на режима) — ключът пази ръчното.
 *
 * Ключовете стоят в `localStorage` (втори преглед на одита): само в паметта
 * презареден екран — точно това, което човек прави, когато „нещо увисне" —
 * забравяше ключа и повторението ставаше нова поръчка. Хранилището е на
 * устройството (общо за табовете му); друг терминал е друго човешко действие
 * и ключ не го разпознава по замисъл. Без хранилище (частен прозорец, забрана)
 * — само паметта, както преди.
 */

export const IDEMPOTENCY_KEY_FIELD = "idempotencyKey";

/** Колко дълго повторение със същото тяло е „същото действие". */
export const IDEMPOTENCY_RETAIN_MS = 10 * 60_000;
const MAX_REMEMBERED = 200;

/** Действията, чието повторение е второ действие с последици извън базата. */
const IDEMPOTENT_ROUTES: RegExp[] = [
  /^\/bills\/?$/, // поръчка — нова маса или допоръчка (кухненски билет, сума)
  /^\/bills\/[^/]+\/cancel-items?$/, // сторно (storno билет, сума)
  /^\/operations\/cash-(in|out)$/, // служебен внос/износ (пари)
];

interface Remembered {
  key: string;
  expiresAt: number;
}

const STORAGE_KEY = "cloudts.idempotencyKeys.v1";

/** Паметта на ТОЗИ таб — и резервата, когато хранилището не е достъпно. */
const remembered = new Map<string, Remembered>();

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isRemembered(value: unknown): value is Remembered {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Remembered).key === "string" &&
    typeof (value as Remembered).expiresAt === "number"
  );
}

/** Записаното от другите табове/преди презареждането влиза в паметта. */
function loadStored() {
  const store = storage();
  if (!store) return;
  try {
    const parsed: unknown = JSON.parse(store.getItem(STORAGE_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return;
    for (const entry of parsed) {
      if (!Array.isArray(entry) || typeof entry[0] !== "string") continue;
      const [fingerprint, value] = entry as [string, unknown];
      // Помненият ключ печели: друг таб може вече да е пратил същото тяло с него.
      if (isRemembered(value)) remembered.set(fingerprint, value);
    }
  } catch {
    // Повредено хранилище — паметта е достатъчна, следващият запис го пренаписва.
  }
}

function saveStored() {
  const store = storage();
  if (!store) return;
  try {
    if (remembered.size === 0) store.removeItem(STORAGE_KEY);
    else store.setItem(STORAGE_KEY, JSON.stringify([...remembered]));
  } catch {
    // Пълно/забранено хранилище — остава паметта.
  }
}

function pathOf(url: string | undefined): string {
  const raw = url ?? "";
  const withoutQuery = raw.split("?")[0];
  // Абсолютен адрес (рядко) → само пътят.
  return withoutQuery.replace(/^[a-z]+:\/\/[^/]+/i, "");
}

export function isIdempotentMutation(
  method: string | undefined,
  url: string | undefined,
): boolean {
  if ((method ?? "get").toLowerCase() !== "post") return false;
  const path = pathOf(url);
  return IDEMPOTENT_ROUTES.some((route) => route.test(path));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/** JSON с подредени ключове — едно и също тяло дава един и същ отпечатък. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .filter((key) => key !== IDEMPOTENCY_KEY_FIELD && value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

export function fingerprintOf(url: string | undefined, data: unknown): string {
  return `${pathOf(url)}|${stableStringify(data)}`;
}

function newKey(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  // uuid v4 от случайни байтове — за среда без `randomUUID`.
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function prune(now: number) {
  for (const [fingerprint, entry] of remembered) {
    if (entry.expiresAt <= now) remembered.delete(fingerprint);
  }
  while (remembered.size > MAX_REMEMBERED) {
    const oldest = remembered.keys().next().value;
    if (oldest === undefined) break;
    remembered.delete(oldest);
  }
}

/**
 * Ключът за това тяло: помненият (повторение след провал с неизвестен изход
 * или двойно натискане, докато първото още е във въздуха) или нов.
 */
export function keyFor(fingerprint: string, now: number = Date.now()): string {
  loadStored();
  prune(now);
  const existing = remembered.get(fingerprint);
  const key = existing ? existing.key : newKey();
  remembered.set(fingerprint, { key, expiresAt: now + IDEMPOTENCY_RETAIN_MS });
  saveStored();
  return key;
}

/** Изходът е КАТЕГОРИЧЕН (успех или отказ на сървъра) — ключът се забравя. */
export function forgetKey(fingerprint: string | undefined) {
  if (!fingerprint) return;
  loadStored();
  remembered.delete(fingerprint);
  saveStored();
}

/** Само за тестове: паметта И хранилището. */
export function resetIdempotencyKeysForTests() {
  remembered.clear();
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // няма хранилище
  }
}

/** Само за тестове: „презареждане" — паметта на таба изчезва, хранилището остава. */
export function forgetTabMemoryForTests() {
  remembered.clear();
}

export { isPlainObject as isPlainBody };
